/**
 * The approval page's states, as pure views (no fetching), so each one is tested and shown on
 * demo data: loading, opening the request, the consent card, words to type, approved, the ended
 * states, not allowed and error. The page (pages/Pair.tsx) holds the requests and picks the view.
 *
 * It reads like a security product's consent screen: one calm card, the computer's facts labelled
 * as reported by it, the key fingerprint large enough to compare, who it joins, one primary action.
 */
import {
  ArrowRight,
  ChevronDown,
  CircleCheck,
  Clock,
  KeyRound,
  Laptop,
  Link2Off,
  OctagonX,
  ServerCog,
  ShieldAlert,
  ShieldCheck,
  TriangleAlert,
  XCircle,
} from '@orgus/ui/icons';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { PAIR_LIMITS } from '@orgus/protocol';
import type { PairHarness, PairRequestView, SignInProvider } from '@orgus/protocol';
import { Button, Card, Checkbox, Collapsible, CollapsibleContent, CollapsibleTrigger, ErrorState, Input, RelativeTime, Skeleton, SkeletonText, cn, copyText } from '@orgus/ui';
import type { UiError } from '@orgus/ui';
import { Logo } from '../../components/shell/Logo.tsx';
import { ProviderMark } from '../auth/ProviderMark.tsx';
import { PAIR_DIFFERENT_NETWORK, PairRequestFacts } from './PairRequestFacts.tsx';

const HARNESS_LABEL: Record<string, string> = { 'claude-code': 'Claude Code', codex: 'Codex', gemini: 'Gemini CLI', cursor: 'Cursor' };

export const harnessName = (id: string): string => HARNESS_LABEL[id] ?? id;
export const signInLabel = (h: PairHarness): string => (h.signedIn === true ? 'Signed in' : h.signedIn === false ? 'Not signed in' : 'Sign-in not known');

/** The full hex fingerprint in groups of eight, so two people can read it out. */
export const hexGroups = (hex: string): string => (hex.match(/.{1,8}/g) ?? []).join(' ');

/** The page frame: the mark, then one column. */
export function PairFrame({ children }: { children: ReactNode }) {
  return (
    <main className="flex min-h-full items-start justify-center bg-bg px-4 py-12 text-fg sm:items-center">
      <div className="flex w-full max-w-120 flex-col gap-4">
        <div className="flex items-center gap-2 text-body font-semibold">
          <Logo size={22} />
          Orgus
        </div>
        {children}
      </div>
    </main>
  );
}

export function PairLoading() {
  return (
    <Card className="flex flex-col gap-6" role="status" aria-busy="true" aria-label="Loading the pairing request">
      <div className="flex flex-col gap-2">
        <Skeleton className="size-10 rounded-card" />
        <Skeleton className="h-7 w-3/4" />
      </div>
      <SkeletonText lines={5} />
      <Skeleton className="h-20 w-full rounded-control" />
      <Skeleton className="h-control-lg w-40" />
    </Card>
  );
}

/** A finished or blocked state: icon, title, why, what next, and an optional action. */
export function PairNotice({ icon, tone = 'neutral', title, why, next, action }: { icon: ReactNode; tone?: 'neutral' | 'success' | 'danger' | 'attention'; title: string; why?: ReactNode; next?: ReactNode; action?: ReactNode }) {
  const toneClass = { neutral: 'text-fg-subtle', success: 'text-passed', danger: 'text-status-danger', attention: 'text-status-attention' }[tone];
  return (
    <Card className="flex flex-col gap-4" role="status">
      <span className={cn('flex size-10 items-center justify-center rounded-card bg-raised [&_svg]:size-5', toneClass)} aria-hidden>
        {icon}
      </span>
      <div className="flex flex-col gap-2">
        <h1 className="text-title text-fg">{title}</h1>
        {why && <p className="text-body text-fg-muted">{why}</p>}
        {next && <p className="text-ui text-fg-subtle">{next}</p>}
      </div>
      {action && <div className="flex flex-wrap gap-2">{action}</div>}
    </Card>
  );
}

/** One labelled fact: the label in a fixed column, the value beside it. */
function Fact({ label, mono, children }: { label: string; mono?: boolean; children: ReactNode }) {
  return (
    <div className="flex gap-4">
      <dt className="w-24 shrink-0 text-fg-subtle">{label}</dt>
      <dd className={cn('flex min-w-0 flex-1 flex-col gap-1 text-fg', mono && 'font-mono')}>{children}</dd>
    </div>
  );
}

/** The computer, as the Add machine dialog shows it: name, System, Account; then its agents. */
function Facts({ view }: { view: PairRequestView }) {
  return (
    <section aria-label="Reported by the computer" className="flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-control bg-raised text-fg-subtle ring-1 ring-border" aria-hidden>
          <Laptop className="size-4" />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-ui font-medium text-fg">{view.machine.hostname} wants to join</p>
          <p className="text-caption text-fg-subtle">Reported by the computer</p>
        </div>
      </div>
      <PairRequestFacts request={view} fingerprint={false} />
      <div className="flex min-w-0 flex-col text-caption">
        <span className="text-fg-subtle">Coding agents</span>
        {view.harnesses.length === 0 && <span className="text-fg-muted">None found</span>}
        {view.harnesses.map((h) => (
          <span key={h.id} className="flex min-w-0 items-center gap-2 text-fg">
            <span className="truncate">
              {harnessName(h.id)}
              {h.version ? <span className="text-fg-subtle"> {h.version}</span> : null}
            </span>
            <span className={cn('flex shrink-0 items-center gap-1', h.signedIn === true ? 'text-fg-muted' : 'text-fg-subtle')}>
              {h.signedIn === true ? <CircleCheck className="size-3 text-passed" aria-hidden /> : <XCircle className="size-3" aria-hidden />}
              {signInLabel(h)}
            </span>
          </span>
        ))}
      </div>
    </section>
  );
}

function Fingerprint({ value, hex }: { value: string; hex?: string }) {
  const [open, setOpen] = useState(false);
  return (
    <section aria-labelledby="pair-key" className="flex flex-col gap-2 rounded-control border border-border-subtle bg-raised p-4">
      <h2 id="pair-key" className="flex items-center gap-2 text-caption font-medium text-fg-subtle">
        <KeyRound className="size-3" aria-hidden />
        Key fingerprint
      </h2>
      <p className="font-mono text-title font-medium text-fg num" data-testid="pair-fingerprint">
        {value}
      </p>
      <p className="text-ui text-fg-muted">Check it matches the one on the computer.</p>
      {hex && (
        <Collapsible open={open} onOpenChange={setOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="-ml-2 self-start" iconRight={<ChevronDown className={cn('transition-transform', open && 'rotate-180')} />}>
              {open ? 'Hide full fingerprint' : 'Show full fingerprint'}
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <p className="pt-2 font-mono text-caption break-all text-fg-muted">SHA-256 {hexGroups(hex)}</p>
          </CollapsibleContent>
        </Collapsible>
      )}
    </section>
  );
}

function ServerIdentity({ view }: { view: PairRequestView }) {
  if (!view.server) return null;
  return (
    <section aria-labelledby="pair-server" className="flex flex-col gap-2">
      <h2 id="pair-server" className="text-caption font-medium text-fg-subtle">
        Server
      </h2>
      <dl className="flex flex-col gap-2 text-caption">
        <Fact label="Address" mono>
          <span className="block truncate">{view.server.domain}</span>
        </Fact>
        <Fact label="Key" mono>
          <span className="block truncate num">{view.server.keyFingerprint}</span>
        </Fact>
      </dl>
    </section>
  );
}

export interface ConsentProps {
  view: PairRequestView;
  name: string;
  onName: (v: string) => void;
  autoUpdate: boolean;
  onAutoUpdate: (v: boolean) => void;
  busy: 'approve' | 'deny' | null;
  actionError: UiError | null;
  onApprove: () => void;
  onDeny: () => void;
}

/** The consent card: what is asking, who it joins, the key to compare, and Approve. */
export function PairConsent({ view, name, onName, autoUpdate, onAutoUpdate, busy, actionError, onApprove, onDeny }: ConsentProps) {
  const org = view.org?.name ?? 'Orgus';
  return (
    <Card className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <span className="flex size-10 items-center justify-center rounded-card bg-accent-soft text-accent-text [&_svg]:size-5" aria-hidden>
          <ShieldCheck />
        </span>
        <div className="flex flex-col gap-1">
          <h1 className="text-title text-fg">Add this computer to {org}?</h1>
          {view.server && (
            <p className="text-ui text-fg-muted">
              It connects to <span className="font-mono text-fg">{view.server.domain}</span>
            </p>
          )}
          {view.mine === false && view.claimedBy && <p className="text-ui text-fg-muted">Opened by {view.claimedBy.name} on the computer</p>}
        </div>
      </div>

      {view.differentNetwork && (
        <div role="alert" className="flex gap-3 rounded-control border border-status-attention-line bg-status-attention-soft p-3">
          <TriangleAlert className="size-4 shrink-0 text-status-attention" aria-hidden />
          <div className="flex flex-col gap-1">
            <p className="text-ui text-status-attention-ink">{PAIR_DIFFERENT_NETWORK}</p>
          </div>
        </div>
      )}

      <Facts view={view} />
      <Fingerprint value={view.fingerprint} hex={view.fingerprintHex} />

      <div className="flex flex-col gap-4">
        <Input label="Name" value={name} onChange={(e) => onName(e.target.value)} placeholder={view.machine.hostname} autoComplete="off" spellCheck={false} maxLength={120} hint="How it appears in Machines." />
        <Checkbox checked={autoUpdate} onCheckedChange={(v) => onAutoUpdate(v)} label="Update automatically" hint="Installs signed Orgus updates as they are released." />
      </div>

      {actionError && <ErrorState variant="inline" error={actionError} />}

      <div className="flex flex-col gap-2 sm:flex-row">
        <Button variant="primary" size="lg" icon={<ShieldCheck />} loading={busy === 'approve'} disabled={busy === 'deny'} onClick={onApprove}>
          Approve {view.machine.hostname}
        </Button>
        <Button variant="secondary" size="lg" loading={busy === 'deny'} disabled={busy === 'approve'} onClick={onDeny}>
          Deny
        </Button>
      </div>

      <div className="flex flex-col gap-4 border-t border-border-subtle pt-4">
        <ServerIdentity view={view} />
        <p className="flex items-center gap-2 text-caption text-fg-subtle">
          <Clock className="size-3 shrink-0" aria-hidden />
          <span>
            Expires <RelativeTime ts={view.expiresAt} className="text-fg-subtle" />. Approving makes it your personal computer and is recorded in the audit log.
          </span>
        </p>
      </div>
    </Card>
  );
}

/** The three words in what a person typed (spaces, commas or dashes between them), or null. */
export function wordsOf(text: string): [string, string, string] | null {
  const w = text.trim().toLowerCase().split(/[\s,-]+/).filter(Boolean);
  return w.length === 3 ? [w[0]!, w[1]!, w[2]!] : null;
}

/**
 * Flow C: the person types the three words the computer shows. Nothing about the request is
 * shown before that (the server shows an unclaimed request to nobody), so the words are the proof.
 */
export function PairWords({ busy, actionError, onWords }: { busy: boolean; actionError: UiError | null; onWords: (w: [string, string, string]) => void }) {
  const [text, setText] = useState('');
  const words = wordsOf(text);
  return (
    <Card className="flex flex-col gap-6">
      <div className="flex flex-col gap-1">
        <h1 className="text-title text-fg">Type the words your computer shows</h1>
        <p className="text-body text-fg-muted">The Orgus installer on the computer you are adding shows three words under this link.</p>
      </div>
      <form
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (words) onWords(words);
        }}
      >
        <Input label="Words" value={text} onChange={(e) => setText(e.target.value)} placeholder="three words" autoComplete="off" autoCapitalize="none" spellCheck={false} maxLength={120} hint="In the order shown, with spaces between them." />
        {actionError && <ErrorState variant="inline" error={actionError} />}
        <Button type="submit" variant="primary" size="lg" loading={busy} disabled={!words}>
          Open request
        </Button>
      </form>
      <p className="text-caption text-fg-subtle">After {PAIR_LIMITS.wordTries} wrong tries the request is declined and your admins are alerted.</p>
    </Card>
  );
}

/**
 * PAIR-WEB-2: the link from the installer is opened only by a click, after the person has seen
 * what is asking. A computer on another network takes one more confirmation.
 */
export function PairOpen({ view, busy, actionError, onOpen }: { view: PairRequestView; busy: boolean; actionError: UiError | null; onOpen: (differentNetworkAck: boolean) => void }) {
  const [mine, setMine] = useState(false);
  const ready = !view.differentNetwork || mine;
  return (
    <Card className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <span className="flex size-10 items-center justify-center rounded-card bg-accent-soft text-accent-text [&_svg]:size-5" aria-hidden>
          <Laptop />
        </span>
        <div className="flex flex-col gap-1">
          <h1 className="text-title text-fg">Is {view.machine.hostname} the computer in front of you?</h1>
          <p className="text-ui text-fg-muted">Open this request only if you started the Orgus installer on it yourself.</p>
        </div>
      </div>
      {view.differentNetwork && (
        <div role="alert" className="flex gap-3 rounded-control border border-status-attention-line bg-status-attention-soft p-3">
          <TriangleAlert className="size-4 shrink-0 text-status-attention" aria-hidden />
          <p className="text-ui text-status-attention-ink">{PAIR_DIFFERENT_NETWORK}</p>
        </div>
      )}
      <section aria-label="Reported by the computer" className="flex flex-col gap-2">
        <p className="text-caption text-fg-subtle">Reported by the computer</p>
        <PairRequestFacts request={view} />
      </section>
      {view.differentNetwork && <Checkbox checked={mine} onCheckedChange={(v) => setMine(v)} label="I started the installer on this computer" hint="Someone may have sent you this link to add their computer to your org." />}
      {actionError && <ErrorState variant="inline" error={actionError} />}
      <Button variant="primary" size="lg" icon={<ArrowRight />} loading={busy} disabled={!ready} onClick={() => onOpen(view.differentNetwork && mine)}>
        Open request
      </Button>
    </Card>
  );
}

/** Paired: what happened, and where to see it. */
export function PairApproved({ view }: { view: PairRequestView }) {
  const name = view.machineName ?? view.machine.hostname;
  return (
    <PairNotice
      icon={<CircleCheck />}
      tone="success"
      title="Paired. You can close this tab"
      why={`${name} is now part of ${view.org?.name ?? 'Orgus'}. It connects on its own in a few seconds.`}
      next={view.decidedBy ? `Approved by ${view.decidedBy.name}.` : undefined}
      action={
        view.machineId ? (
          <Button variant="secondary" iconRight={<ArrowRight />} onClick={() => window.location.assign(`/machines?machine=${encodeURIComponent(view.machineId!)}`)}>
            View computer
          </Button>
        ) : undefined
      }
    />
  );
}

/** The ended states, each with what to do next. */
export function PairEnded({ view }: { view: PairRequestView }) {
  switch (view.state) {
    case 'expired':
      return <PairNotice icon={<Clock />} title="This request expired" why="A computer must be approved within 10 minutes." next="Run the Orgus installer on the computer again. It opens a new link." />;
    case 'denied':
      return <PairNotice icon={<OctagonX />} title="Request declined" why={view.decidedBy ? `${view.decidedBy.name} declined ${view.machine.hostname}.` : `${view.machine.hostname} was declined.`} next="If that was a mistake, run the installer on the computer again." />;
    case 'conflict':
      return <PairNotice icon={<ShieldAlert />} tone="danger" title="Stopped for safety" why="This link was used in a second browser, so the request was stopped and your admins were alerted." next="Run the installer on the computer again, and open the link only in the browser it opens." />;
    case 'cancelled':
      return <PairNotice icon={<XCircle />} title="Cancelled on the computer" why="The installer was closed, or it started a new request." next="Use the newest window the installer opened, or run it again." />;
    case 'approved':
      return <PairApproved view={view} />;
    default:
      return null;
  }
}

export interface SignInCardProps {
  /** What the code from the link showed (peek), or null when there is no code or it could not be read. */
  view: PairRequestView | null;
  /** The sign-in methods the server offers, in order; empty: only the owner sign-in link. */
  providers: readonly SignInProvider[];
  busy: string | null;
  error: UiError | null;
  onProvider: (p: SignInProvider) => void;
  onOwnerLink: (link: string) => void;
}

/**
 * Signed out: a short card, "Sign in to approve <machine>", with the same facts, and the sign-in
 * methods the server offers. Sign-in comes straight back here, where one click opens the request.
 */
export function PairSignIn({ view, providers, busy, error, onProvider, onOwnerLink }: SignInCardProps) {
  const [link, setLink] = useState('');
  const name = view?.machine.hostname;
  return (
    <Card className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <span className="flex size-10 items-center justify-center rounded-card bg-accent-soft text-accent-text [&_svg]:size-5" aria-hidden>
          <ShieldCheck />
        </span>
        <div className="flex flex-col gap-1">
          <h1 className="text-title text-fg">{name ? `Sign in to approve ${name}` : 'Sign in to approve this computer'}</h1>
          {view?.server && (
            <p className="text-ui text-fg-muted">
              It connects to <span className="font-mono text-fg">{view.server.domain}</span>
            </p>
          )}
        </div>
      </div>
      {view && (
        <section aria-label="Reported by the computer" className="flex flex-col gap-2">
          <p className="text-caption text-fg-subtle">Reported by the computer</p>
          <PairRequestFacts request={view} />
        </section>
      )}
      {error && <ErrorState variant="inline" error={error} />}
      {providers.length > 0 ? (
        <div className="flex flex-col gap-2">
          {providers.map((p, i) => (
            <Button key={p.id} variant={i === 0 ? 'primary' : 'secondary'} size="lg" icon={<ProviderMark id={p.id} />} className="w-full justify-center" loading={busy === p.id} disabled={busy !== null && busy !== p.id} onClick={() => onProvider(p)}>
              Sign in with {p.label}
            </Button>
          ))}
        </div>
      ) : (
        <form
          className="flex flex-col gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            onOwnerLink(link);
          }}
        >
          <Input label="Sign-in link" value={link} onChange={(e) => setLink(e.target.value)} placeholder="http://127.0.0.1:4600/#token=..." autoComplete="off" spellCheck={false} hint="Run orgus token on the hub computer and paste the link it prints." />
          <Button type="submit" variant="primary" size="lg" loading={busy === 'link'}>
            Sign in with link
          </Button>
        </form>
      )}
      <p className="text-caption text-fg-subtle">You come straight back here to approve it.</p>
    </Card>
  );
}

/** Opened without the code from the installer (another device, or a copied link). */
export function PairNoCode() {
  return (
    <PairNotice
      icon={<Link2Off />}
      title="Open this link on the computer you are adding"
      why="The link the installer opens carries a one-time code that proves you are at that computer. This address does not have it."
      next="Use the browser window the installer opened on that computer."
    />
  );
}

/**
 * A member opened the request: an admin approves it (R-SC-2, ORGU-30). The page refreshes on its
 * own, so it turns into the approved view when they do. The link carries no code: only an admin,
 * or this person, can open it.
 */
export function PairAwaitingAdmin({ view }: { view: PairRequestView }) {
  const [copied, setCopied] = useState<boolean | null>(null);
  const link = () => `${window.location.origin}/pair/${encodeURIComponent(view.requestId)}`;
  return (
    <PairNotice
      icon={<Clock />}
      title="Waiting for an admin"
      why={`Every new computer needs an admin's approval. ${view.machine.hostname} joins as soon as one approves it.`}
      next={copied === false ? 'The browser blocked the clipboard. Copy the address of this page instead.' : 'Send an admin this page. It updates on its own.'}
      action={
        <Button variant="secondary" onClick={() => void copyText(link()).then(setCopied)}>
          {copied ? 'Copied' : 'Copy link for an admin'}
        </Button>
      }
    />
  );
}

/** Signed in, but not someone who may decide this request. */
export function PairNotAllowed({ error }: { error: UiError }) {
  return <PairNotice icon={<ServerCog />} title={error.what} why={error.why} next={error.next} />;
}

/** The request could not be loaded or acted on: what failed, why, what next, and Retry. */
export function PairError({ error, onRetry }: { error: unknown; onRetry?: () => void | Promise<void> }) {
  return (
    <Card>
      <ErrorState error={error} onRetry={onRetry} />
    </Card>
  );
}
